import {PermissionResource} from '../../../data/workbench-api/generated/types'

export type Resource = {
  id: PermissionResource
  label: string
  description?: string
  status?: 'preview' | 'beta'
  actions?: {
    // false prevents the option showing, string provides a label
    view?: string | false
    edit?: string | false
    admin?: string | false
  }
}

export type PermissionDetail = Partial<Resource>

export type PermissionDetailMap = Partial<
  Record<PermissionResource, PermissionDetail | false>
>

export const permissionDetail: PermissionDetailMap = {
  [PermissionResource.Account]: false,
  [PermissionResource.AuthAccount]: false,
  [PermissionResource.TriggerEvent]: false,
  [PermissionResource.BusinessUserInvite]: false,
  [PermissionResource.EndUserTag]: false,
  [PermissionResource.ResourceUser]: false,
  [PermissionResource.ActivityLog]: {
    label: 'Request debugger',
    description: 'Log of the last 100 failed client API requests',
    actions: {
      edit: false,
      admin: false
    }
  },
  [PermissionResource.AnalyticsBatch]: {
    label: 'Analytics exporter',
    description:
      'Batched analytics files for the last 30 days available to download',
    actions: {
      edit: false,
      admin: false
    }
  },
  [PermissionResource.AuditLog]: {
    description: 'Log of workbench members’ actions within your organization',
    status: 'beta',
    actions: {
      view: 'View own audit log entries',
      edit: false,
      admin: "View others' audit log entries"
    }
  },
  [PermissionResource.BusinessUser]: {
    label: 'Workbench member',
    description: 'A member of the workbench',
    actions: {
      admin: false
    }
  },
  [PermissionResource.BusinessUserGroup]: {
    label: 'Workbench member group assignment',
    description: 'Controls assignment of workbench members to groups',
    actions: {
      admin: false
    }
  },
  [PermissionResource.CardApproval]: {
    label: 'Override card approval',
    description: 'Enables a member to approve on behalf of any approval group',
    actions: {
      view: false,
      edit: false,
      admin: 'Enables a member to approve on behalf of any approval group'
    }
  },
  [PermissionResource.CardInstance]: {
    description: 'An instance of a card template sent to a customer',
    actions: {
      view: 'View log of created card instances (metadata only), and view card analytics',
      edit: 'Send published cards to customers from within the workbench',
      admin: 'Cancel sent cards'
    }
  },
  [PermissionResource.CardTemplate]: {
    description: 'Layout and configuration for a card',
    actions: {
      view: 'View, send test cards,	view media',
      edit: 'Edit unpublished cards templates, edit media',
      admin: 'Publish and archive card templates'
    }
  },
  [PermissionResource.ClientKey]: {
    label: 'SDK API key',
    description:
      'Needed for SDK integrations, and to configure allowed JWT issuers',
    actions: {
      admin: false
    }
  },
  [PermissionResource.CustomRole]: {
    label: 'Role',
    description:
      'An identity with specific permissions. A role can be assigned to 1 or more groups, giving a set of permissions to all members of that group',
    actions: {
      view: 'View all created roles and their permissions',
      edit: 'Update existing and create new roles',
      admin: false
    }
  },
  [PermissionResource.CustomerToken]: {
    label: 'API Authentication control',
    description: 'Used to authenticate your requests to the Atomic API',
    actions: {
      view: 'List authentication controls',
      edit: 'List and create authentication controls',
      admin: false
    }
  },
  [PermissionResource.EndUser]: {
    label: 'Customer',
    description: 'A user of your web and mobile applications',
    actions: {
      view: false,
      edit: false,
      admin: 'View and update profile data'
    }
  },
  [PermissionResource.EndUserSegment]: {
    label: 'Segment',
    description: 'Subset of customers with common characteristics',
    actions: {
      view: 'View which filters are applied to the segment',
      edit: 'Update existing and create new segments',
      admin: false
    }
  },
  [PermissionResource.Environment]: {
    description:
      'Each environment within an organization is a separate space, with separate configuration',
    actions: {
      edit: 'Create, update name',
      admin: 'Deactivate environment and edit custom fields'
    }
  },
  [PermissionResource.EventDefinition]: {
    description: 'Defines the schema for a legacy event',
    actions: {
      admin: false
    }
  },
  [PermissionResource.Group]: {
    label: 'Workbench member group',
    description: 'Configuration of user groups and their associated roles',
    actions: {
      view: 'View all created groups and their roles',
      edit: 'Update existing and create new groups',
      admin: false
    }
  },
  [PermissionResource.Organisation]: {
    label: 'Organization',
    description:
      'Atomic customers are typically given one Organization. Each organization contains multiple Environments',
    actions: {
      view: 'View organization name and preferences',
      edit: 'Update organization name and preferences',
      admin: 'Update organization name, preferences and data retention period'
    }
  },
  [PermissionResource.SnsPlatform]: {
    label: 'Notifications',
    description:
      'Push notification configuration details for each SDK platform',
    actions: {
      admin: false
    }
  },
  [PermissionResource.Stream]: {
    label: 'Stream',
    description:
      'A collection of cards which can be assigned to 1 or more stream containers in your apps',
    actions: {
      admin: false
    }
  },
  [PermissionResource.StreamContainer]: {
    label: 'Container',
    description: 'Configures card streams within your app',
    actions: {
      admin: false
    }
  },
  [PermissionResource.Tag]: {
    description: 'Tags which are available when filtering customers',
    actions: {
      admin: false
    }
  },
  [PermissionResource.Theme]: {
    description: 'Theming configuration for stream containers',
    actions: {
      admin: false
    }
  },
  [PermissionResource.WebhookCredential]: {
    description: 'Authentication credentials for webhook requests',
    actions: {
      admin: false
    }
  },
  [PermissionResource.WebhooksEndpoint]: {
    label: 'Webhook subscription',
    description: 'Requests made from Atomic to external services',
    actions: {
      admin: false
    }
  },
  [PermissionResource.WebhookLog]: {
    label: 'Webhook request log',
    description: 'Status and metadata for outgoing webhook requests',
    actions: {
      view: 'View metadata',
      edit: false,
      admin: false
    }
  },
  [PermissionResource.ActionFlow]: {
    label: 'Action Flow configuration',
    description:
      'Action Flow configuration, this permission also controls connector and segment triggers on cards.',
    actions: {
      admin: 'Publish Action Flow configuration'
    }
  }
}
